import { AloftArea, expected, schAloftArea, schSourceType } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import { isSubjectSiteSizeSourceConflict } from '../../../../modules/alerts/definitions/utils/compAdjustmentMateriality.ts';
import { resolveFeatureGridFieldConflicts } from '../../../../redux/features/actions/resolveFeatureGridFieldConflicts.ts';
import {
  selectFeatureGridActiveConflictedFields,
  selectFeatureGridConflictedFields,
} from '../../../../redux/features/selectors/uiSelectors.ts';
import { addFeatureGridConflictedField } from '../../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { PropertyGridFieldName } from '../../types.ts';
import { SourceOption } from './types.ts';

const GLA_FIELD_NAME = 'editableData.improvements.gla';
const GLA_CONFLICT_THRESHOLD = 0.05;
const SITE_AREA_FIELD_NAME = 'editableData.site.area';

const areaOptionsConflict = (options: SourceOption[], threshold: number): boolean => {
  const sqftValues = options
    .map((o) => schAloftArea.optional().parse(o.rawValue)?.toSqFt().unitAmount)
    .filter((v): v is number => v !== undefined);
  if (sqftValues.length < 2) return false;
  const min = Math.min(...sqftValues);
  const max = Math.max(...sqftValues);
  if (min === 0) return false;
  return (max - min) / min > threshold;
};

export const glaOptionsConflict = (options: SourceOption[]): boolean =>
  areaOptionsConflict(options, GLA_CONFLICT_THRESHOLD);

const siteAreaOptionsConflict = (options: SourceOption[]): boolean => {
  const areas = options
    .map((o) => schAloftArea.optional().parse(o.rawValue))
    .filter((area): area is AloftArea => area !== undefined);
  return isSubjectSiteSizeSourceConflict(areas);
};

const ADDED_FEATURE_AREA_PATTERN =
  /^editableData\.(?:arv\.)?improvements\.([^.]+)\.area$/;

export const shouldIgnoreAddedFeatureAreaConflict = (fieldName: string): boolean => {
  const match = ADDED_FEATURE_AREA_PATTERN.exec(fieldName);
  return match !== null && match[1] !== 'basement';
};

type UseFieldConflictDetectionParams = {
  propertyDataFieldName: string;
  fieldName: PropertyGridFieldName;
  propertyId: string;
  fieldHasConflict: boolean;
  conflictIsResolved?: boolean;
  options: SourceOption[];
};

export const useFieldConflictDetection = ({
  propertyDataFieldName,
  fieldName,
  propertyId,
  fieldHasConflict,
  conflictIsResolved,
  options,
}: UseFieldConflictDetectionParams) => {
  const dispatch = useAppDispatch();
  const activeConflictedFields = useAppSelector(selectFeatureGridActiveConflictedFields);
  const allConflictedFields = useAppSelector(selectFeatureGridConflictedFields);

  const optionsKey = options.map((o) => `${o.source}:${o.value ?? ''}`).join('|');

  useEffect(() => {
    if (options.length === 0 || fieldHasConflict) return;

    const filteredOptions = options.filter(
      (option) =>
        option.value !== undefined &&
        `${option.value}` !== '0' &&
        option.source !== schSourceType.enum.unknown,
    );
    if (filteredOptions.length === 0) return;

    let hasConflict = false;
    if (fieldName === GLA_FIELD_NAME) {
      hasConflict = glaOptionsConflict(filteredOptions);
    } else if (fieldName === SITE_AREA_FIELD_NAME) {
      hasConflict = siteAreaOptionsConflict(filteredOptions);
    } else if (shouldIgnoreAddedFeatureAreaConflict(fieldName)) {
      hasConflict = false;
    } else {
      hasConflict = filteredOptions.some((o) => o.value !== expected(filteredOptions[0]).value);
    }

    if (hasConflict) {
      dispatch(
        addFeatureGridConflictedField({ [propertyDataFieldName]: { fieldName, propertyId } }),
      );
    }
  }, [
    dispatch,
    fieldHasConflict,
    fieldName,
    optionsKey,
    propertyDataFieldName,
    propertyId,
  ]);

  useEffect(() => {
    if (!conflictIsResolved) return;

    const fieldIsInAllConflicts = propertyDataFieldName in allConflictedFields;
    const fieldIsInActiveConflicts = propertyDataFieldName in activeConflictedFields;

    if (!fieldIsInAllConflicts) {
      dispatch(
        addFeatureGridConflictedField({ [propertyDataFieldName]: { fieldName, propertyId } }),
      );
    }

    if (fieldIsInActiveConflicts) {
      const activeField = activeConflictedFields[propertyDataFieldName];
      if (activeField) {
        dispatch(
          resolveFeatureGridFieldConflicts({
            [propertyDataFieldName]: activeField,
          }),
        );
      }
    }
  }, [
    conflictIsResolved,
    propertyDataFieldName,
    fieldName,
    propertyId,
    allConflictedFields,
    activeConflictedFields,
    dispatch,
  ]);
};
